import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { projectsAPI, clientsAPI, usersAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import {
  Plus, X, FolderKanban, ArrowRight, Calendar, Edit2,
  Search, LayoutGrid, List, Users as UsersIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import ArchiveButton from '../../components/ArchiveButton';

const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

function ProjectModal({ project, initialLocation, clients, users, onSave, onClose }) {
  const isEdit = Boolean(project?.id);
  // Orden alfabético para que las listas de selección sean predecibles
  // a medida que el equipo crece (#MIRq5Vy3).
  const _byName = (a, b) => {
    const an = (a.name || `${a.first_name || ''} ${a.last_name || ''}`).trim();
    const bn = (b.name || `${b.first_name || ''} ${b.last_name || ''}`).trim();
    return an.localeCompare(bn, 'es', { sensitivity: 'base' });
  };
  const leaders = users.filter(u => u.role === 'lider_proyecto' || u.role === 'super_admin').sort(_byName);
  // #IncludeAdminsAsLevantadores — admins también pueden fungir como
  // levantadores asignados a un proyecto.
  const levantadores = users.filter(u => ['super_admin', 'lider_proyecto', 'levantador'].includes(u.role) && u.is_active !== false).sort(_byName);
  const [form, setForm] = useState(
    project
      ? { name: project.name || '', client_id: project.client_id || '', description: project.description || '', deadline: project.deadline?.slice(0,10) || '', leader_id: project.leader_id || '', levantadores: project.levantadores || [] }
      : { name: '', client_id: initialLocation?.client_id || '', description: '', deadline: '', leader_id: '', levantadores: [], ...(initialLocation ? { location_id: initialLocation.id } : {}) }
  );
  const hasInitialLocation = !isEdit && Boolean(form.location_id && initialLocation);
  const [saving, setSaving] = useState(false);
  // #LevantadoresPicker — estado del dropdown "agregar levantador" estilo
  // tag-picker: el usuario escoge uno por uno desde el select y cada
  // selección aparece como chip removible debajo. Mucho más usable que
  // el <select multiple> nativo (que requería Ctrl+clic y se rompía en
  // móvil).
  const [pendingLev, setPendingLev] = useState('');

  const addLevantador = (id) => {
    if (!id) return;
    setForm(f => f.levantadores.includes(id)
      ? f
      : { ...f, levantadores: [...f.levantadores, id] });
    setPendingLev('');
  };
  const removeLevantador = (id) => {
    setForm(f => ({ ...f, levantadores: f.levantadores.filter(x => x !== id) }));
  };

  // Lista de levantadores disponibles (no asignados aún)
  const availableLev = levantadores.filter(u => !form.levantadores.includes(u.id));
  const userById = (id) => levantadores.find(u => u.id === id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.deadline) delete payload.deadline;
      if (!payload.leader_id) delete payload.leader_id;
      if (!payload.levantadores?.length) delete payload.levantadores;
      await onSave(payload);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar proyecto');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" data-testid="project-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-soem-navy">
          <h3 className="font-bold text-white">{isEdit ? 'Editar Proyecto' : 'Nuevo Proyecto'}</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={20} className="text-slate-400 hover:text-white" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label htmlFor="f-nombre-del-proyecto" className="soem-label block mb-1">Nombre del proyecto <span className="text-red-500">*</span></label>
            <input id="f-nombre-del-proyecto" className={inputCls} value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="project-name-input" />
          </div>
          {hasInitialLocation && (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3" data-testid="project-location">
              <p className="soem-label">Locación</p>
              <p className="text-sm text-soem-navy">{initialLocation.name}</p>
              <button type="button" className="text-sm text-soem-primary" onClick={() => setForm(f => {
                const { location_id, ...rest } = f;
                return rest;
              })}>Quitar locación</button>
            </div>
          )}
          <div>
            <label htmlFor="f-cliente" className="soem-label block mb-1">Cliente <span className="text-red-500">*</span></label>
            <select id="f-cliente" className={inputCls} value={form.client_id} disabled={hasInitialLocation && Boolean(initialLocation.client_id)} onChange={e => setForm({...form, client_id: e.target.value})} required data-testid="project-client-select">
              <option value="">— Seleccionar cliente —</option>
              {hasInitialLocation && initialLocation.client_id && !clients.some(c => c.id === initialLocation.client_id) && <option value={initialLocation.client_id}>Cliente de la locación</option>}
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="f-lider-asignado" className="soem-label block mb-1">Líder asignado</label>
            <select id="f-lider-asignado" className={inputCls} value={form.leader_id} onChange={e => setForm({...form, leader_id: e.target.value})} data-testid="project-leader-select">
              <option value="">— Seleccionar líder —</option>
              {leaders.map(u => <option key={u.id} value={u.id}>{u.name || `${u.first_name || ''} ${u.last_name || ''}`}</option>)}
            </select>
          </div>
          {levantadores.length > 0 && (
            <div>
              <label htmlFor="f-levantadores" className="soem-label block mb-1">Levantadores (opcional)</label>
              {/* #LevantadoresPicker — dropdown estilo "Líder Asignado" donde
                  cada selección agrega un chip a la lista de abajo. Ya no
                  exigimos Ctrl+clic ni multi-select nativo. */}
              <select
                id="f-levantadores"
                className={inputCls}
                value={pendingLev}
                onChange={(e) => addLevantador(e.target.value)}
                disabled={availableLev.length === 0}
                data-testid="project-levantador-add"
              >
                <option value="">
                  {availableLev.length === 0
                    ? '— Todos los levantadores ya están asignados —'
                    : '— Agregar levantador —'}
                </option>
                {availableLev.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name || `${u.first_name || ''} ${u.last_name || ''}`}
                  </option>
                ))}
              </select>
              {form.levantadores.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {form.levantadores.map((id) => {
                    const u = userById(id);
                    const label = u
                      ? (u.name || `${u.first_name || ''} ${u.last_name || ''}`)
                      : id.slice(0, 8);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs px-2 py-1 rounded-full font-medium"
                        data-testid={`project-levantador-chip-${id}`}
                      >
                        {label}
                        <button
                          type="button"
                          onClick={() => removeLevantador(id)}
                          aria-label={`Quitar ${label}`}
                          className="hover:text-red-600"
                        >
                          <X size={11} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-slate-400 mt-1">
                Selecciona uno por uno del dropdown — aparecen como etiquetas removibles.
                {form.levantadores.length > 0 && ` · ${form.levantadores.length} seleccionado(s)`}
              </p>
            </div>
          )}
          <div>
            <label htmlFor="f-descripción" className="soem-label block mb-1">Descripción</label>
            <textarea id="f-descripción" className={`${inputCls} resize-none`} rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="project-description-input" />
          </div>
          <div>
            <label htmlFor="f-fecha-límite-opcional" className="soem-label block mb-1">Fecha límite (opcional)</label>
            <input id="f-fecha-límite-opcional" type="date" className={inputCls} value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} data-testid="project-deadline-input" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="soem-btn-primary flex-1 py-2 text-sm disabled:opacity-60" data-testid="project-save-btn">
              {saving ? 'Guardando...' : isEdit ? 'Actualizar Proyecto' : 'Crear Proyecto'}
            </button>
            <button type="button" onClick={onClose} className="soem-btn-secondary flex-1 py-2 text-sm">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const { isAdmin, isLider, canDo } = useAuth();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [initialLocation, setInitialLocation] = useState(null);
  const canCreate = canDo('create_project');

  useEffect(() => {
    const state = routeLocation.state;
    if (!canCreate || typeof state?.location_id !== 'string' || !state.location_id) return;
    const unavailable = state.status && state.status !== 'activa';
    if (unavailable) toast.error('La locación no está activa. Elige una locación activa para crear el proyecto.');
    setInitialLocation(unavailable ? null : {
      id: state.location_id,
      client_id: state.client_id || '',
      status: state.status,
      name: typeof state.location_name === 'string' && state.location_name.trim()
        ? state.location_name : 'Locación seleccionada',
    });
    setShowModal(!unavailable);
    // Consumir solo esta intención: cancelar o volver no debe reabrir el alta.
    const { location_id, location_name, client_id, status, ...rest } = state;
    navigate(`${routeLocation.pathname}${routeLocation.search || ''}${routeLocation.hash || ''}`, {
      replace: true, state: Object.keys(rest).length ? rest : null,
    });
  }, [canCreate, routeLocation, navigate]);
  // #DashboardSearch — buscador estilo Eventos, filtra por nombre,
  // descripción o cliente.
  const [search, setSearch] = useState('');
  // #ProjectsViewToggle — vista tarjetas/lista persistida en localStorage,
  // igual que Clientes.
  const [view, setView] = useState(() => localStorage.getItem('projects_view') || 'cards');
  const changeView = (v) => { setView(v); localStorage.setItem('projects_view', v); };

  const load = async () => {
    setLoading(true);
    try {
      // Project listing must work even when the caller (Levantador) cannot
      // read the global clients/users catalogs. Backend project enrichment
      // already includes client + leader info, so the catalogs are only
      // needed by the "Nuevo Proyecto" modal.
      const empty = { data: { items: [] } };
      const [p, c, u] = await Promise.all([
        projectsAPI.list(),
        clientsAPI.list().catch(() => empty),
        usersAPI.list().catch(() => empty),
      ]);
      // #ChronoOrder — más reciente arriba. Backend ya manda sorted por
      // -created_at, pero garantizamos client-side por si llega cacheado
      // o desordenado.
      const items = (p.data.items || [])
        .filter(i => !i.is_archived)
        .sort((a, b) => {
          const ad = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bd = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bd - ad;
        });
      setProjects(items);
      setClients(c.data.items || []);
      setUsers(u.data.items || []);
    } catch { toast.error('Error al cargar proyectos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (form) => {
    // El backend ProjectCreate ignora `levantadores`; los persiste el
    // endpoint /assign-levantador (#hrTGmL — antes el campo seleccionado
    // en el modal se descartaba en silencio).
    const { levantadores = [], ...rest } = form;
    const r = await projectsAPI.create(rest);
    const newId = r?.data?.id;
    if (newId && levantadores.length) {
      const failed = [];
      for (const uid of levantadores) {
        try { await projectsAPI.assignLevantador(newId, uid); }
        catch { failed.push(uid); }
      }
      if (failed.length) toast.error(`${failed.length} levantador(es) no se asignaron`);
    }
    toast.success('Proyecto creado');
    load();
  };

  const handleEdit = async (form) => {
    // Edición desde Projects.jsx solo persiste los campos aceptados por
    // ProjectUpdate (name/description/deadline/leader_id/etc); las
    // levantadores se gestionan desde ProjectDetail con assignLevantador.
    const { levantadores: _ignored, ...rest } = form;
    await projectsAPI.update(editProject.id, rest);
    toast.success('Proyecto actualizado');
    load();
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || id;

  // #DashboardSearch — filtra por nombre, descripción y nombre del cliente.
  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => {
      const cli = clients.find(c => c.id === p.client_id)?.name || '';
      const fields = [p.name, p.description, cli].filter(Boolean).map(s => String(s).toLowerCase());
      return fields.some(f => f.includes(q));
    });
  }, [projects, clients, search]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Proyectos</h1>
          <p className="text-slate-500 text-sm mt-1">
            {filteredProjects.length === projects.length
              ? `${projects.length} proyectos`
              : `${filteredProjects.length} de ${projects.length} proyectos`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* #DashboardSearch — buscador inline */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="pl-9 pr-8 py-2 border border-slate-300 rounded-sm text-sm w-60 focus:outline-none focus:ring-2 focus:ring-soem-primary"
              placeholder="Buscar proyecto..."
              aria-label="Buscar proyectos"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="projects-search"
            />
            {search && (
              <button
                type="button"
                aria-label="Limpiar búsqueda"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={13} />
              </button>
            )}
          </div>
          {/* #ProjectsViewToggle — toggle tarjetas/lista */}
          <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden">
            <button onClick={() => changeView('cards')}
              className={`p-2 ${view === 'cards' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}
              data-testid="projects-view-cards" title="Vista tarjetas">
              <LayoutGrid size={15} />
            </button>
            <button onClick={() => changeView('table')}
              className={`p-2 ${view === 'table' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}
              data-testid="projects-view-table" title="Vista lista">
              <List size={15} />
            </button>
          </div>
          {canDo('create_project') && (
            <button onClick={() => { setInitialLocation(null); setShowModal(true); }} id="btn-nuevo-proyecto" className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="create-project-btn">
              <Plus size={16} /> Nuevo Proyecto
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[...Array(4)].map((_, i) => <div key={i} className="soem-card h-36 animate-pulse bg-slate-100" />)}
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="soem-card p-12 text-center">
          <FolderKanban size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">
            {search ? `No se encontraron proyectos para "${search}".` : 'No hay proyectos disponibles.'}
          </p>
        </div>
      ) : view === 'table' ? (
        <div className="soem-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Proyecto</th>
                <th className="px-4 py-3 text-left">Cliente</th>
                <th className="px-4 py-3 text-left">Líder</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-left">Deadline</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProjects.map((p) => {
                const leader = users.find(u => u.id === p.leader_id);
                const leaderName = leader
                  ? (leader.name || `${leader.first_name || ''} ${leader.last_name || ''}`.trim())
                  : null;
                let deadlineEl = <span className="text-slate-300 text-xs">—</span>;
                if (p.deadline) {
                  const today = new Date();
                  const due = new Date(p.deadline);
                  const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
                  let label, color;
                  if (diff < 0) { label = `Vencido hace ${Math.abs(diff)}d`; color = 'text-red-600 font-semibold'; }
                  else if (diff === 0) { label = 'Vence hoy'; color = 'text-orange-600 font-semibold'; }
                  else if (diff <= 7) { label = `${diff}d restantes`; color = 'text-orange-500 font-semibold'; }
                  else { label = due.toLocaleDateString('es-MX'); color = 'text-slate-500'; }
                  deadlineEl = <span className={`text-xs ${color}`}>{label}</span>;
                }
                return (
                  <tr key={p.id} className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/dashboard/proyectos/${p.id}`)}
                      data-testid={`project-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-soem-navy">{p.name}</p>
                      {p.description && <p className="text-xs text-slate-400 line-clamp-1 max-w-xs">{p.description}</p>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{clientName(p.client_id)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{leaderName || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.status === 'activo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">{deadlineEl}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        {canDo('edit_project') && (
                          <button
                            onClick={e => { e.stopPropagation(); setEditProject(p); }}
                            className="text-slate-400 hover:text-soem-primary"
                            title="Editar proyecto"
                            data-testid={`edit-project-${p.id}`}>
                            <Edit2 size={15} />
                          </button>
                        )}
                        {isAdmin && (
                          <span onClick={(e) => e.stopPropagation()}>
                            <ArchiveButton entity="projects" id={p.id} name={p.name} onArchived={load} />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filteredProjects.map(p => (
            <div key={p.id} className="soem-card-accent hover:shadow-md transition-shadow p-5"
              data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 cursor-pointer" onClick={() => navigate(`/dashboard/proyectos/${p.id}`)}>
                  <h3 className="font-bold text-soem-navy text-base">{p.name}</h3>
                  <p className="text-slate-400 text-xs mt-0.5">{clientName(p.client_id)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.status === 'activo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {p.status}
                  </span>
                  {canDo('edit_project') && (
                    <button
                      onClick={e => { e.stopPropagation(); setEditProject(p); }}
                      className="p-1.5 text-slate-400 hover:text-soem-primary rounded-sm hover:bg-blue-50 transition-colors"
                      title="Editar proyecto"
                      data-testid={`edit-project-${p.id}`}>
                      <Edit2 size={14} />
                    </button>
                  )}
                  {isAdmin && <ArchiveButton entity="projects" id={p.id} name={p.name} onArchived={load} />}
                </div>
              </div>
              {p.description && <p className="text-slate-500 text-sm mb-3 line-clamp-2">{p.description}</p>}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs">
                  {p.deadline ? (() => {
                    const today = new Date();
                    const due = new Date(p.deadline);
                    const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
                    let label, color;
                    if (diff < 0) { label = `Vencido hace ${Math.abs(diff)}d`; color = 'text-red-600 font-semibold'; }
                    else if (diff === 0) { label = 'Vence hoy'; color = 'text-orange-600 font-semibold'; }
                    else if (diff <= 7) { label = `${diff}d restantes`; color = 'text-orange-500 font-semibold'; }
                    else { label = due.toLocaleDateString('es-MX'); color = 'text-slate-400'; }
                    return <div className={`flex items-center gap-1 ${color}`}><Calendar size={12} /><span>{label}</span></div>;
                  })() : <span className="text-slate-300 text-xs">Sin deadline</span>}
                </div>
                <button onClick={() => navigate(`/dashboard/proyectos/${p.id}`)}
                  className="flex items-center gap-1.5 text-soem-primary text-xs font-semibold">
                  Ver detalle <ArrowRight size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <ProjectModal initialLocation={initialLocation} clients={clients} users={users} onSave={handleCreate} onClose={() => setShowModal(false)} />
      )}
      {editProject && (
        <ProjectModal project={editProject} clients={clients} users={users} onSave={handleEdit} onClose={() => setEditProject(null)} />
      )}
    </div>
  );
}
